import { createComponent, createContext, useContext, createEffect, createSignal } from "solid-js";
import { isServer } from "solid-js/web";
import {
  LANGUAGES,
  SUPPORTED_SEGMENTS,
  DEFAULT_SEGMENT,
  localeForSegment,
  segmentForLocale,
  segmentFromPath,
  localeFromPath,
  prefixFromPath,
} from "@/locales/registry";

function dictFrom(mod, code) {
  const d = mod?.[code];
  if (!d) throw new Error(`[i18n] src/locales/${code}.js must export a binding named "${code}"`);
  return d;
}

export const DICT = {};

const [dictVersion, setDictVersion] = createSignal(0);

const LOADERS = import.meta.env.SSR ? {} : import.meta.glob("@/locales/*.js");

export async function loadDict(code) {
  if (DICT[code] || import.meta.env.SSR) return;
  const path = Object.keys(LOADERS).find((p) => p.endsWith(`/${code}.js`));
  if (!path) throw new Error(`[i18n] no locale module for "${code}" (expected src/locales/${code}.js)`);
  const mod = await LOADERS[path]();
  DICT[code] = dictFrom(mod, code);
  setDictVersion((v) => v + 1);
}

export { dictVersion };

export {
  SUPPORTED_SEGMENTS,
  DEFAULT_SEGMENT,
  localeForSegment,
  segmentForLocale,
  segmentFromPath,
  localeFromPath,
  prefixFromPath,
};

const LocaleContext = createContext("en");

export function LocaleProvider(props) {
  createEffect(() => {
    if (isServer) return;
    const code = props.locale ?? "en";
    const lang = LANGUAGES.find((l) => l.code === code);
    document.documentElement.lang = lang?.hreflang ?? "en";
    document.documentElement.dir = lang?.rtl ? "rtl" : "ltr";
    loadDict("en");
    loadDict(code);
  });
  return createComponent(LocaleContext.Provider, {
    get value() {
      return props.locale ?? "en";
    },
    get children() {
      return props.children;
    },
  });
}

export const hardcoded = (text) => text;

export function useLocale() {
  const locale = useContext(LocaleContext);
  const t = (key) => {
    dictVersion();
    const dict = DICT[locale];
    if (!dict) return "";
    let val: unknown = dict;
    for (const part of key.split(".")) val = val?.[part];
    if (val == null) {
      let enVal: unknown = DICT.en;
      for (const part of key.split(".")) enVal = enVal?.[part];
      return enVal ?? key;
    }
    return val;
  };
  return { locale, t, prefix: `/${segmentForLocale(locale)}` };
}
